// NOTE: overlay removed to stop on-screen debug lines
// import "./debug/overlay";

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import ColumnView from "./components/ColumnView";

// Run on main thread
import { simulateColumn } from "./lib/sim/CounterCurrentColumnSimulator";

// Load crude definitions
import { listCrudes, getCrudeSet } from "./lib/thermo/pseudocomponents/loaders";

import { setEosKLogging } from "./lib/thermo/EOSK";

// Crude-specific initial settings (column inputs + draw presets)
import {
  DEFAULT_DRAW_NAMES_BY_TRAY_32,
  getCrudeInitialSettings,
  getRecommendedFeedTray,
} from "./config/crudeInitialSettings";

// Enable EOS-K debug logging (keep after imports to satisfy eslint import/first)
setEosKLogging(true);

function buildDefaultDrawConfig(crudeName, trays = 32) {
  const cfg = getCrudeInitialSettings(crudeName);
  const specs0 = cfg?.drawSpecsByTrayInde0 || {};
  // Build a row per draw in descending tray order (top -> bottom)
  return Object.keys(specs0)
    .map((k) => Number.parseInt(k, 10))
    .filter((i0) => Number.isFinite(i0) && i0 >= 0 && i0 < trays)
    .sort((a, b) => b - a)
    .map((i0) => {
      const tray = i0 + 1; // 1-based
      const frac = Number(specs0[i0]) || 0;
      const pct = 100 * frac;
      const name =
        DEFAULT_DRAW_NAMES_BY_TRAY_32[tray] ||
        (tray === trays
          ? "Overhead"
          : tray === 1
          ? "Residue"
          : `Draw @ Tray ${tray}`);
      return { id: `${crudeName}-${tray}`, name, tray, pct };
    });
}

function safeParseJSON(str, fallback) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

const DRAW_STORE_KEY = "adt_drawConfigByCrude_v1";

function loadDrawStore() {
  if (typeof window === "undefined") return {};
  return safeParseJSON(window.localStorage.getItem(DRAW_STORE_KEY) || "{}", {});
}

function saveDrawStore(store) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DRAW_STORE_KEY, JSON.stringify(store || {}));
  } catch {
    // ignore quota / private mode failures
  }
}

/* -------------------- tiny helpers -------------------- */
function normalize(vec) {
  const s = vec.reduce((a, b) => a + b, 0) || 1;
  return vec.map((v) => Math.max(0, v / s));
}

function uniformZ(n) {
  return normalize(Array.from({ length: n }, () => 1));
}

function formatPower(kW) {
  if (kW == null || !isFinite(kW)) return "—";
  const abs = Math.abs(kW);
  if (abs >= 1000) return `${(kW / 1000).toFixed(2)} MW`;
  return `${kW.toFixed(1)} kW`;
}

function pct(x) {
  if (x == null || !isFinite(x)) return "—";
  return `${(100 * x).toFixed(1)}%`;
}

/* -------------------- Solver diagnostics (UI-only) -------------------- */
function DiagnosticsPanel({ diagnostics }) {
  const items = Array.isArray(diagnostics) ? diagnostics : [];

  // Default visible height ~3 text lines, but scrollable for long output.
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ paddingBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>Solver diagnostics</h3>
      </div>
      <div className="card-body" style={{ paddingTop: 8 }}>
        <div
          className="diagnostics-window"
          style={{
            maxHeight: "calc(3 * 1.45em + 16px)", // 3 full lines + top/bottom padding (8px+8px)
            overflowY: "auto",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.10)",
            padding: "8px 10px",
            background: "rgba(255,255,255,0.03)",
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          {items.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {items.map((d, i) => {
                const lvl = String(d?.level || "info").toLowerCase();
                const msg = String(d?.message || "");
                const icon =
                  lvl === "error"
                    ? "⛔"
                    : lvl === "warning" || lvl === "warn"
                    ? "⚠️"
                    : "ℹ️";
                return (
                  <div key={i} className={`diag-line diag-${lvl}`}>
                    <span style={{ marginRight: 8 }}>{icon}</span>
                    <span>{msg}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="muted">No diagnostics.</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Draw basis (%, kg/h) -------------------- */
const DRAW_BASIS_KEY = "adt_draw_basis";

function loadDrawBasis() {
  try {
    const v = localStorage.getItem(DRAW_BASIS_KEY);
    return v === "kgph" ? "kgph" : "pct";
  } catch {
    return "pct";
  }
}

function saveDrawBasis(v) {
  try {
    localStorage.setItem(DRAW_BASIS_KEY, v);
  } catch {
    // ignore
  }
}

/* -------------------- Run log search helpers -------------------- */
function escapeRegExp(str) {
  // Escape user input so it can be safely used inside a RegExp
  // eslint-disable-next-line no-useless-escape
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function findMatchPositions(
  logText,
  query,
  caseSensitive = false,
  maxMatches = 2000
) {
  if (!query) return [];
  const needle = String(query);
  if (!needle) return [];
  const flags = caseSensitive ? "g" : "gi";
  let re;
  try {
    re = new RegExp(escapeRegExp(needle), flags);
  } catch (e) {
    return [];
  }
  const out = [];
  let m;
  while (out.length < maxMatches && (m = re.exec(logText)) !== null) {
    out.push(m.index);
    // Avoid infinite loops on zero-length matches (shouldn't happen due to escape, but safe)
    if (m[0].length === 0) re.lastIndex += 1;
  }
  return out;
}

/* -------------------- Temperature spike analysis -------------------- */
/** Returns { spikeTrays: number[], notes: string[] } where spikeTrays are 1-based. */
function analyzeTemperatureSpikes(result, opts = {}) {
  const trays = Array.isArray(result?.trays) ? result.trays : [];
  const N = trays.length;
  const spikeTol = opts.spikeTol ?? 60; // K deviation vs neighbors' mean
  const monoTol = opts.monoTol ?? 25; // K allowed upward jump
  const btmTol = opts.btmTol ?? 60; // K bottom vs reboiler
  const topTol = opts.topTol ?? 40; // K top vs condenser

  const spikeTrays = [];
  const notes = [];
  if (N === 0) return { spikeTrays, notes };

  const TbottomTray = trays[0]?.T;
  const TtopTray = trays[N - 1]?.T;
  const Treb = result?.boundary?.reboiler?.T_hot_K;
  const Tcond = result?.boundary?.condenser?.T_cold_K;

  const condenserType = String(
    result?.energy?.condenser?.condenserType || "total"
  ).toLowerCase();

  // A) Boundary agreement
  if (Number.isFinite(Treb) && Number.isFinite(TbottomTray)) {
    const d = Math.abs(Treb - TbottomTray);
    if (d > btmTol) {
      notes.push(
        `Warning: bottom tray T (${TbottomTray.toFixed(
          1
        )} K) differs from reboiler T_hot (${Treb.toFixed(1)} K) by ${d.toFixed(
          1
        )} K.`
      );
      spikeTrays.push(1);
    }
  }
  if (Number.isFinite(Tcond) && Number.isFinite(TtopTray)) {
    const d = Math.abs(Tcond - TtopTray);
    if (d > topTol) {
      // In a total condenser, the reported "top tray" temperature can represent
      // the equilibrium at the column top / condenser inlet, while T_cold is the
      // condenser outlet (often subcooled). A delta here can be expected.
      const lvl = condenserType === "total" ? "Info" : "Warning";
      notes.push(
        `${lvl}: top tray T (${TtopTray.toFixed(
          1
        )} K) differs from condenser T_cold (${Tcond.toFixed(
          1
        )} K) by ${d.toFixed(1)} K.`
      );
      spikeTrays.push(N);
    }
  }

  // B) Monotonicity & C) Local spikes
  for (let i = 0; i < N; i++) {
    const Ti = trays[i]?.T;
    if (!Number.isFinite(Ti)) continue;

    // B) Non-monotonic jump going up the column
    if (i < N - 1) {
      const Tnext = trays[i + 1]?.T;
      if (Number.isFinite(Tnext) && Tnext - Ti > monoTol) {
        notes.push(
          `Warning: non-monotonic rise between Tray ${i + 1} (${Ti.toFixed(
            1
          )} K) and Tray ${i + 2} (${Tnext.toFixed(1)} K), Δ=${(
            Tnext - Ti
          ).toFixed(1)} K.`
        );
        spikeTrays.push(i + 2);
      }
    }

    // C) Local spike vs neighbors’ mean
    if (i > 0 && i < N - 1) {
      const Tm1 = trays[i - 1]?.T;
      const Tp1 = trays[i + 1]?.T;
      if (Number.isFinite(Tm1) && Number.isFinite(Tp1)) {
        const m = 0.5 * (Tm1 + Tp1);
        const d = Math.abs(Ti - m);
        if (d > spikeTol) {
          notes.push(
            `Warning: local spike at Tray ${i + 1}: T=${Ti.toFixed(
              1
            )} K deviates ${d.toFixed(1)} K from neighbors’ mean ${m.toFixed(
              1
            )} K.`
          );
          spikeTrays.push(i + 1);
        }
      }
    }
  }

  const uniq = [...new Set(spikeTrays)].sort((a, b) => a - b);
  return { spikeTrays: uniq, notes };
}

export default function App() {
  /* -------------------- inputs -------------------- */
  const CRUDE_NAMES = useMemo(() => listCrudes(), []);
  const [crude, setCrude] = useState(
    CRUDE_NAMES[0] || "Western Canadian Select"
  );
  const [eosMode, setEosMode] = useState("auto");
  const [eosManual, setEosManual] = useState("PRSV");

  // UI helper: show which spec is currently *active* (used by the solver)
  // without preventing the user from typing "working" values into the other
  // spec fields (HYSYS-style).
  const specFieldClass = (isActive) =>
    isActive ? "spec-active" : "spec-inactive";

  // --- Editable draw configuration (per crude) ---
  const [drawStore, setDrawStore] = useState(() => loadDrawStore());

  // Ensure current crude has a draw config (defaults if missing)
  useEffect(() => {
    if (!crude) return;
    setDrawStore((prev) => {
      if (prev?.[crude]?.length) return prev;
      const next = { ...(prev || {}) };
      next[crude] = buildDefaultDrawConfig(crude, 32);
      saveDrawStore(next);
      return next;
    });
  }, [crude]);

  const drawConfig = useMemo(() => {
    const rows = drawStore?.[crude];
    return Array.isArray(rows) && rows.length
      ? rows
      : buildDefaultDrawConfig(crude, 32);
  }, [drawStore, crude]);

  const setDrawConfig = useCallback(
    (updater) => {
      setDrawStore((prev) => {
        const next = { ...(prev || {}) };
        const cur = Array.isArray(next[crude])
          ? next[crude]
          : buildDefaultDrawConfig(crude, 32);
        const updated = typeof updater === "function" ? updater(cur) : updater;
        next[crude] = Array.isArray(updated) ? updated : cur;
        saveDrawStore(next);
        return next;
      });
    },
    [crude]
  );

  const drawSpecsUI = useMemo(() => {
    // 0-based tray index -> fraction of feed
    const out = {};
    for (const row of drawConfig) {
      const tray = Number(row?.tray);
      const pctVal = Number(row?.pct);
      if (!Number.isFinite(tray) || tray < 1 || tray > 32) continue;
      if (!Number.isFinite(pctVal) || pctVal < 0) continue;
      const i0 = tray - 1;
      out[i0] = pctVal / 100;
    }
    return out;
  }, [drawConfig]);

  const drawLabelsUI = useMemo(() => {
    // 1-based tray -> label
    const out = {};
    for (const row of drawConfig) {
      const tray = Number(row?.tray);
      const name = String(row?.name || "").trim();
      if (!Number.isFinite(tray) || tray < 1 || tray > 32) continue;
      if (!name) continue;
      out[tray] = name;
    }
    return out;
  }, [drawConfig]);

  const drawTraysUI = useMemo(() => {
    // 0-based tray indices (for highlighting)
    const trays = [];
    for (const row of drawConfig) {
      const tray = Number(row?.tray);
      if (Number.isFinite(tray) && tray >= 1 && tray <= 32)
        trays.push(tray - 1);
    }
    // Unique + sort
    return Array.from(new Set(trays)).sort((a, b) => a - b);
  }, [drawConfig]);

  const drawTotalPct = useMemo(() => {
    return drawConfig.reduce((s, r) => s + (Number(r?.pct) || 0), 0);
  }, [drawConfig]);

  useEffect(() => {
    if (!crude) return;
    const cfg = getCrudeInitialSettings(crude);

    // Temperatures
    if (Number.isFinite(cfg?.Tfeed_K)) setTfeed(cfg.Tfeed_K);
    if (Number.isFinite(cfg?.feedTray)) {
      setFeedTray(cfg.feedTray);
    } else {
      setFeedTray(getRecommendedFeedTray(crude, 32));
    }
    if (Number.isFinite(cfg?.Tbottom_K)) setTbottom(cfg.Tbottom_K);
    if (Number.isFinite(cfg?.Ttop_K)) setTtop(cfg.Ttop_K);

    // Pressure
    if (Number.isFinite(cfg?.Ptop_Pa)) setPtop(cfg.Ptop_Pa);
    if (Number.isFinite(cfg?.dP_perTray_Pa)) setPdrop(cfg.dP_perTray_Pa);

    // Specs + ratios
    if (typeof cfg?.condenserSpec === "string")
      setCondenserSpec(cfg.condenserSpec);
    if (typeof cfg?.reboilerSpec === "string")
      setReboilerSpec(cfg.reboilerSpec);
    if (typeof cfg?.condenserType === "string")
      setCondenserType(cfg.condenserType);
    else setCondenserType("total");
    if (typeof cfg?.reboilerType === "string")
      setReboilerType(cfg.reboilerType);
    else setReboilerType("partial");
    if (Number.isFinite(cfg?.Qc_kW)) setQc_kW(cfg.Qc_kW);
    if (Number.isFinite(cfg?.Qr_kW)) setQr_kW(cfg.Qr_kW);
    if (Number.isFinite(cfg?.refluxRatio)) setRefluxRatio(cfg.refluxRatio);
    if (Number.isFinite(cfg?.reboilRatio)) setReboilRatio(cfg.reboilRatio);

    // Murphree
    const m = cfg?.murphree;
    if (m && typeof m === "object") {
      if (Number.isFinite(m.etaV_top)) setEtaVTop(m.etaV_top);
      if (Number.isFinite(m.etaV_mid)) setEtaVMid(m.etaV_mid);
      if (Number.isFinite(m.etaV_bot)) setEtaVBot(m.etaV_bot);
      if (Number.isFinite(m.etaL_top)) setEtaLTop(m.etaL_top);
      if (Number.isFinite(m.etaL_mid)) setEtaLMid(m.etaL_mid);
      if (Number.isFinite(m.etaL_bot)) setEtaLBot(m.etaL_bot);
    }
  }, [crude]);

  const [feedRate, setFeedRate] = useState(100000); // kg/h (display & mass-balance basis)
  const [drawBasis, setDrawBasis] = useState(() => loadDrawBasis()); // 'pct' | 'kgph'

  useEffect(() => {
    saveDrawBasis(drawBasis);
  }, [drawBasis]);
  // Sensible ADU/CDU starting guesses (these get overridden when a crude is selected)
  const [Tfeed, setTfeed] = useState(628);
  const [feedTray, setFeedTray] = useState(() =>
    getRecommendedFeedTray("Brent", 32)
  );
  const [Ttop, setTtop] = useState(398);
  const [Tbottom, setTbottom] = useState(618);
  const [Ptop, setPtop] = useState(150000);
  const [Pdrop, setPdrop] = useState(200);

  const [refluxRatio, setRefluxRatio] = useState(2.0);
  const [reboilRatio, setReboilRatio] = useState(0.06);
  const [Qr_kW, setQr_kW] = useState(6000); // fixed reboiler duty (kW)

  // Boundary spec selectors
  // Condenser energy: 'temperature' | 'duty'
  // Reboiler: 'duty' | 'temperature' | 'boilup'
  const [condenserSpec, setCondenserSpec] = useState("temperature");
  const [reboilerSpec, setReboilerSpec] = useState("duty");

  // Equipment type (HYSYS-like)
  // - Total condenser: all overhead vapor is condensed to liquid
  // - Partial condenser: allows an overhead vapor product (future extension)
  const [condenserType, setCondenserType] = useState("total");
  // - Partial reboiler: caps boilup so some bottoms liquid remains
  // - Total reboiler: allows higher boilup (still prevents exactly zero bottoms)
  const [reboilerType, setReboilerType] = useState("partial");
  const [Qc_kW, setQc_kW] = useState(-6000); // condenser duty (kW, negative)

  // Murphree efficiencies
  const [etaV_top, setEtaVTop] = useState(0.75);
  const [etaV_mid, setEtaVMid] = useState(0.65);
  const [etaV_bot, setEtaVBot] = useState(0.55);
  const [showLiquidEta, setShowLiquidEta] = useState(false);
  const [etaL_top, setEtaLTop] = useState(1.0);
  const [etaL_mid, setEtaLMid] = useState(1.0);
  const [etaL_bot, setEtaLBot] = useState(1.0);

  const murphree = useMemo(
    () => ({
      etaV_top,
      etaV_mid,
      etaV_bot,
      etaL_top: showLiquidEta ? etaL_top : 1.0,
      etaL_mid: showLiquidEta ? etaL_mid : 1.0,
      etaL_bot: showLiquidEta ? etaL_bot : 1.0,
    }),
    [etaV_top, etaV_mid, etaV_bot, showLiquidEta, etaL_top, etaL_mid, etaL_bot]
  );

  const crudeSet = useMemo(() => getCrudeSet(crude), [crude]);
  const components = crudeSet.components;

  const feedZ = useMemo(() => {
    if (
      Array.isArray(crudeSet.zDefault) &&
      crudeSet.zDefault.length === components.length
    ) {
      return normalize(crudeSet.zDefault.slice());
    }
    return uniformZ(components.length);
  }, [crudeSet, components.length]);

  /* -------------------- state -------------------- */
  const [solving, setSolving] = useState(false);
  const [result, setResult] = useState({
    trays: [],
    // Tray numbering: 1 = bottom/reboiler. We treat tray 4 as the fixed
    // "flash zone" / crude feed location (consistent across crude presets).
    feedTray: 4,
    draws: {},
    status: "—",
    energy: {},
  });

  // Run log: where all the old overlay/tick messages go now
  const [runLog, setRunLog] = useState("");

  // Prevent UI lockups when the log gets huge (search + textarea rendering).
  // Keep only the last ~200k characters.
  const MAX_RUNLOG_CHARS = 200_000;

  function trimLogPreserveLines(s) {
    if (s.length <= MAX_RUNLOG_CHARS) return s;

    // Keep a little extra so we can cut cleanly at a newline
    const keep = MAX_RUNLOG_CHARS;
    const start = s.length - keep;

    // Cut from start, but advance to the next line break so we don't start mid-line.
    // (Some logs can have very long lines; don't assume the newline is "close".)
    const slice = s.slice(start);
    const nlN = slice.indexOf("\n");
    const nlR = slice.indexOf("\r");
    let nl;
    if (nlN === -1) nl = nlR;
    else if (nlR === -1) nl = nlN;
    else nl = Math.min(nlN, nlR);

    if (nl >= 0) {
      // Skip the line break(s)
      let i = nl;
      while (i < slice.length && (slice[i] === "\n" || slice[i] === "\r")) i++;
      return slice.slice(i);
    }
    // Fallback: no line breaks at all — just return slice
    return slice;
  }

  function appendLog(line) {
    const s = String(line ?? "");
    // Ensure log entries are separated (avoids a single mega-line that breaks trimming/search UX)
    const withNl = s.endsWith("\n") ? s : s + "\n";
    setRunLog((prev) => trimLogPreserveLines(prev + withNl));
  }

  const clearLog = useCallback(() => setRunLog(""), []);

  // --- Run log search state ---
  // We keep a "draft" (what the user is typing) and a "committed" query (what the log is filtered/highlighted by).
  // This makes search run ONLY when the user presses Enter.
  const [runLogSearchDraft, setRunLogSearchDraft] = useState("");
  const [runLogSearch, setRunLogSearch] = useState(""); // committed query (press Enter to update)
  const [runLogCaseSensitive, setRunLogCaseSensitive] = useState(false);
  const [runLogMatchIndex, setRunLogMatchIndex] = useState(0);
  const runLogTextAreaRef = useRef(null);

  const commitRunLogSearch = useCallback(() => {
    setRunLogSearch(String(runLogSearchDraft || "").trim());
  }, [runLogSearchDraft]);

  const clearRunLogSearch = useCallback(() => {
    setRunLogSearchDraft("");
    setRunLogSearch("");
    setRunLogMatchIndex(0);
  }, []);

  const runLogMatchPositions = useMemo(() => {
    return findMatchPositions(runLog, runLogSearch, runLogCaseSensitive, 5000);
  }, [runLog, runLogSearch, runLogCaseSensitive]);

  const runLogMatchCount = runLogMatchPositions.length;

  useEffect(() => {
    setRunLogMatchIndex(0);
  }, [runLogSearch, runLogCaseSensitive]);

  useEffect(() => {
    if (!runLogSearch || runLogMatchCount <= 0) return;
    const ta = runLogTextAreaRef.current;
    if (!ta) return;
    const idx = Math.min(Math.max(runLogMatchIndex, 0), runLogMatchCount - 1);
    const start = runLogMatchPositions[idx];
    const end = start + runLogSearch.length;
    // Highlight the current match and ensure it is visible (textarea scrolls automatically)
    try {
      ta.focus({ preventScroll: true });
      ta.setSelectionRange(start, end);
    } catch (e) {
      // ignore selection errors
    }
  }, [runLogMatchIndex, runLogSearch, runLogMatchCount, runLogMatchPositions]);

  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setDirty(true);
  }, [
    crude,
    feedRate,
    Tfeed,
    Ttop,
    Tbottom,
    Ptop,
    Pdrop,
    refluxRatio,
    reboilRatio,
    condenserSpec,
    reboilerSpec,
    condenserType,
    reboilerType,
    Qc_kW,
    etaV_top,
    etaV_mid,
    etaV_bot,
    showLiquidEta,
    etaL_top,
    etaL_mid,
    etaL_bot,
    components,
    feedZ,
    drawStore,
  ]);

  /* ---------- console tap for main-thread to capture [PH ...] etc ---------- */
  const tapRestoreRef = useRef(null);

  const tapConsoleToRunLog = () => {
    if (tapRestoreRef.current) return; // already tapped
    const saved = {};
    const toStringSafe = (x) => {
      if (typeof x === "string") return x;
      try {
        return JSON.stringify(x);
      } catch {
        return String(x);
      }
    };

    const shouldPipe = () => true;

    ["log", "info", "warn", "error", "debug"].forEach((lvl) => {
      saved[lvl] = console[lvl].bind(console);
      console[lvl] = (...args) => {
        const text = args.map(toStringSafe).join(" ");
        if (shouldPipe(text)) appendLog(text);
        saved[lvl](...args);
      };
    });

    tapRestoreRef.current = () => {
      ["log", "info", "warn", "error", "debug"].forEach((lvl) => {
        if (saved[lvl]) console[lvl] = saved[lvl];
      });
      tapRestoreRef.current = null;
    };
  };
  const untapConsole = () => {
    if (tapRestoreRef.current) tapRestoreRef.current();
  };

  /* -------------------- run (main-thread only) -------------------- */
  const buildBasePayload = () => ({
    // Preserve the crude key in the payload so the simulator/thermo layer can
    // apply crude-specific safeguards without changing default behavior.
    crudeKey: crude,
    components,
    feedZ,
    feedTray: Math.min(32, Math.max(1, Math.round(Number(feedTray) || 1))),
    Tfeed,
    Ttop,
    Tbottom,
    Ptop,
    Pdrop,
    refluxRatio,
    reboilRatio,
    condenserSpec,
    reboilerSpec,
    condenserType,
    reboilerType,
    Qc_kW,
    Qr_kW,
    trays: 32,
    eosMode,
    eosManual,
    murphree,
    debug: { enabled: true, iterPrint: 1, trayPrint: 1 },
  });

  const handleSolve = () => {
    clearLog();
    appendLog("Starting…");

    const base = buildBasePayload();

    // Build simulator-side draws: keep ONLY mid-column trays (exclude 0 & N-1)
    const N = base.trays || 32;
    const fullCrudeDraws = drawSpecsUI || {};
    const sideDrawSpecs = Object.fromEntries(
      Object.entries(fullCrudeDraws).filter(([k]) => {
        const idx = +k;
        return Number.isFinite(idx) && idx > 0 && idx < N - 1;
      })
    );

    // Main-thread execution
    const payload = {
      ...base,
      feedRate_kgph: feedRate,
      sideDrawSpecs,
      drawLabels: drawLabelsUI,
      eosMode,
      eosManual,
      onProgress: (p) => {
        const st = p?.stage;
        if (!st) return;
        if (st === "init") appendLog("[init]");
        else if (st === "iter")
          appendLog(
            `iter=${p.iter}  Ttop=${(p.Ttop || 0).toFixed(2)}  Tbot=${(
              p.Tbot || 0
            ).toFixed(2)}`
          );
        else if (st === "trayStart")
          appendLog(`trayStart ${p.tray}/${p.trays}`);
        else if (st === "trayProgress")
          appendLog(`trayProgress ${p.tray}/${p.trays}`);
        else if (st === "controller") {
          const bits = [];
          if (p.Qc_kW != null) bits.push(`Qc=${p.Qc_kW.toFixed(1)} kW`);
          if (p.Qr_kW != null) bits.push(`Qr=${p.Qr_kW.toFixed(1)} kW`);
          if (p.e_top != null) bits.push(`e_top=${p.e_top.toFixed(2)}`);
          if (p.e_bot != null) bits.push(`e_bot=${p.e_bot.toFixed(2)}`);
          if (p.tol != null) bits.push(`tol=${p.tol.toExponential(2)}`);
          if (bits.length) appendLog(bits.join("  "));
        } else if (st === "converged") {
          appendLog(
            `[converged] iter=${p.iter} tol=${
              p.tol != null ? p.tol.toExponential(3) : "—"
            }`
          );
        } else if (st === "final") appendLog("[final]");
      },
    };

    setSolving(true);
    setTimeout(() => {
      tapConsoleToRunLog();
      try {
        const out = simulateColumn(payload);

        // ---- NEW: analyze temps for spikes and log warnings
        const flags = analyzeTemperatureSpikes(out);
        if (flags.notes.length) {
          appendLog("---- Temperature quality checks ----");
          flags.notes.forEach((n) => appendLog(n));
        } else {
          appendLog("Temperature quality checks: OK (no spikes detected).");
        }

        // Add a HYSYS-like diagnostic explanation for the common condenser
        // "top temperature spike" condition when using a TOTAL condenser.
        // Keep all diagnostics in the scrollable diagnostics panel.
        const diagnostics = Array.isArray(out?.diagnostics)
          ? [...out.diagnostics]
          : [];
        try {
          const N = Array.isArray(out?.trays) ? out.trays.length : 0;
          const condType = String(
            out?.energy?.condenser?.condenserType || "total"
          ).toLowerCase();
          // If the UI's temperature-spike detector flags the top stage (Tray N)
          // and we're using a TOTAL condenser, add a clear HYSYS-like explanation.
          // (This is common even when the "top tray vs T_cold" delta is modest,
          // because Tray N represents a condenser boundary condition, not a true tray.)
          const topSpike = N > 0 && flags?.spikeTrays?.includes(N);

          if (topSpike && condType === "total") {
            const Tcold = Number(out?.energy?.condenser?.Tcold_K);
            const Ttop = Number(out?.runSummary?.TtopTray_K ?? out?.TtopTray_K);
            const dT =
              Number.isFinite(Tcold) && Number.isFinite(Ttop)
                ? Math.abs(Ttop - Tcold)
                : null;

            diagnostics.push({
              level: "info",
              message:
                "Temperature 'spike' at Tray " +
                N +
                " is usually expected for a TOTAL condenser: Tray " +
                N +
                " is a condenser boundary (V forced to 0) and may report an equilibrium / inlet temperature that won't follow the internal tray profile. " +
                (dT != null
                  ? `Here |T(top) - T_cold| ≈ ${dT.toFixed(1)} K.`
                  : "") +
                " This typically reflects condenser outlet subcooling / reflux enthalpy, not an internal column instability.",
            });
          }
        } catch (_) {
          /* diagnostics are best-effort */
        }

        // attach flags for ColumnView
        setResult({ ...out, flags, diagnostics });
        appendLog(`Status: ${out?.status ?? "OK"}`);
        appendLog("Done.");
      } catch (e) {
        console.error("[MAIN ERROR]", e?.message || e);
        appendLog("Error: " + (e?.message || "failed"));
        setResult((prev) => ({
          ...prev,
          status: "Error: " + (e?.message || "failed"),
        }));
      } finally {
        untapConsole();
        setSolving(false);
        setDirty(false);
      }
    }, 0);
  };
  /* -------------------- render -------------------- */
  const energy = result.energy || {};
  const specs = energy.specs || {};
  const condSpec = specs.condenser || {};
  const rebSpec = specs.reboiler || {};

  // HYSYS-like hint: a crude-specific recommended feed tray.
  // (User can still override the actual feed tray used by the solver.)
  const recommendedFeedTray = getRecommendedFeedTray(crude, 32);

  return (
    <div className="app">
      <div className="app-header">
        <h1>
          Crude Column Simulator - PH flash + EOS K, Murphree efficiencies.
          Feed/Draw trays selectable.
        </h1>
      </div>

      {/* Controls */}
      <div className="card controls">
        <div className="card-body">
          <div className="grid grid-3">
            <div className="form-row">
              <label className="form-label">Crude</label>
              <select
                value={crude}
                onChange={(e) => setCrude(e.target.value)}
                className="form-input  crude-select"
                disabled={solving}
              >
                {CRUDE_NAMES.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label className="form-label">EOS mode</label>
              <select
                value={eosMode}
                onChange={(e) => setEosMode(e.target.value)}
                className="form-input"
                disabled={solving}
              >
                <option value="auto">Auto (by tray/crude)</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            <div className="form-row">
              <label className="form-label">Manual EOS</label>
              <select
                value={eosManual}
                onChange={(e) => setEosManual(e.target.value)}
                className="form-input"
                disabled={solving || eosMode !== "manual"}
                title={
                  eosMode !== "manual"
                    ? "Switch EOS mode to Manual to enable"
                    : ""
                }
              >
                <option value="PR">PR</option>
                <option value="PRSV">PRSV</option>
                <option value="SRK">SRK</option>
              </select>
            </div>

            {/* Feed row: keep Feed rate, Feed tray, Recommended tray, and Feed T on a single line */}
            <div className="spec-row">
              <div className="grid grid-4 feed-row">
                <div className="form-row">
                  <label className="form-label">Feed rate (kg/h)</label>
                  <input
                    type="number"
                    value={feedRate}
                    onChange={(e) => setFeedRate(+e.target.value || 0)}
                    className="form-input"
                    disabled={solving}
                  />
                </div>

                <div className="form-row">
                  <label className="form-label">Feed tray</label>
                  <input
                    type="number"
                    value={feedTray}
                    onChange={(e) => setFeedTray(+e.target.value || 0)}
                    className="form-input"
                    disabled={solving}
                    min={1}
                    max={32}
                  />
                </div>

                <div className="form-row">
                  <label className="form-label">Recommended tray</label>
                  <div className="recommended-tray">
                    <span>
                      <strong>{recommendedFeedTray}</strong>
                    </span>
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => setFeedTray(recommendedFeedTray)}
                      disabled={solving}
                      title="Set feed tray to the recommended value"
                    >
                      Use
                    </button>
                  </div>
                </div>

                <div className="form-row">
                  <label className="form-label">Feed T (K)</label>
                  <input
                    type="number"
                    value={Tfeed}
                    onChange={(e) => setTfeed(+e.target.value || 0)}
                    className="form-input  temp-input"
                    disabled={solving}
                  />
                </div>
              </div>
            </div>

            {/* Condenser/Reboiler specs (full-width rows) */}
            <div className="spec-row">
              <div className="spec-grid spec-grid--5">
                <div className="form-row">
                  <label className="form-label">Condenser spec</label>
                  <select
                    value={condenserSpec}
                    onChange={(e) => setCondenserSpec(e.target.value)}
                    className="form-input"
                    disabled={solving}
                  >
                    <option value="temperature">Temperature (Tc)</option>
                    <option value="duty">Duty (Qc)</option>
                    <option value="reflux">Reflux ratio (L0/D)</option>
                  </select>
                </div>

                <div className="form-row">
                  <label className="form-label">Condenser type</label>
                  <select
                    className="form-input"
                    value={condenserType}
                    onChange={(e) => setCondenserType(e.target.value)}
                    disabled={solving}
                  >
                    <option value="total">Total condenser</option>
                    <option value="partial">Partial condenser</option>
                  </select>
                </div>

                <div className="form-row">
                  <label className="form-label">Top condenser T (K)</label>
                  <input
                    type="number"
                    value={Ttop}
                    onChange={(e) => setTtop(+e.target.value || 0)}
                    className={`form-input temp-input ${specFieldClass(
                      condenserSpec === "temperature"
                    )}`}
                    disabled={solving}
                  />
                  {condenserSpec !== "temperature" &&
                    Number.isFinite(condSpec?.Tc_calc_K) && (
                      <div className="muted small">
                        calc: {condSpec.Tc_calc_K.toFixed(1)} K
                      </div>
                    )}
                </div>

                <div className="form-row">
                  <label className="form-label">Condenser duty Qc (kW)</label>
                  <input
                    type="number"
                    step="100"
                    value={Qc_kW}
                    onChange={(e) => setQc_kW(+e.target.value || 0)}
                    className={`form-input ${specFieldClass(
                      condenserSpec === "duty"
                    )}`}
                    disabled={solving}
                  />
                  {condenserSpec !== "duty" &&
                    Number.isFinite(condSpec?.Qc_calc_kW) && (
                      <div className="muted small">
                        calc: {condSpec.Qc_calc_kW.toFixed(1)} kW
                      </div>
                    )}
                </div>

                <div className="form-row">
                  <label className="form-label">Reflux ratio (L0/D)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={refluxRatio}
                    onChange={(e) => setRefluxRatio(+e.target.value || 0)}
                    className={`form-input ${specFieldClass(
                      condenserSpec === "reflux"
                    )}`}
                    disabled={solving}
                  />
                  {condenserSpec !== "reflux" &&
                    Number.isFinite(condSpec?.refluxRatio_calc) && (
                      <div className="muted small">
                        calc: {condSpec.refluxRatio_calc.toFixed(3)}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div className="spec-row">
              <div className="spec-grid spec-grid--5">
                <div className="form-row">
                  <label className="form-label">Reboiler spec</label>
                  <select
                    value={reboilerSpec}
                    onChange={(e) => setReboilerSpec(e.target.value)}
                    className="form-input"
                    disabled={solving}
                  >
                    <option value="duty">Duty (Qr)</option>
                    <option value="temperature">Temperature (Treb)</option>
                    <option value="boilup">Boilup ratio (Vb/B)</option>
                  </select>
                </div>

                <div className="form-row">
                  <label className="form-label">Reboiler type</label>
                  <select
                    className="form-input"
                    value={reboilerType}
                    onChange={(e) => setReboilerType(e.target.value)}
                  >
                    <option value="partial">Partial reboiler</option>
                    <option value="total">Total reboiler</option>
                  </select>
                </div>

                <div className="form-row">
                  <label className="form-label">Bottom T set (K)</label>
                  <input
                    type="number"
                    value={Tbottom}
                    onChange={(e) => setTbottom(+e.target.value || 0)}
                    className={`form-input temp-input ${specFieldClass(
                      reboilerSpec === "temperature"
                    )}`}
                    disabled={solving}
                  />
                  {reboilerSpec !== "temperature" &&
                    Number.isFinite(rebSpec?.Treb_calc_K) && (
                      <div className="muted small">
                        calc: {rebSpec.Treb_calc_K.toFixed(1)} K
                      </div>
                    )}
                </div>

                <div className="form-row">
                  <label className="form-label">Reboiler duty Qr (kW)</label>
                  <input
                    type="number"
                    step="100"
                    value={Qr_kW}
                    onChange={(e) => setQr_kW(+e.target.value || 0)}
                    className={`form-input ${specFieldClass(
                      reboilerSpec === "duty"
                    )}`}
                    disabled={solving}
                  />
                  {reboilerSpec !== "duty" &&
                    Number.isFinite(rebSpec?.Qr_calc_kW) && (
                      <div className="muted small">
                        calc: {rebSpec.Qr_calc_kW.toFixed(1)} kW
                      </div>
                    )}
                </div>

                <div className="form-row">
                  <label className="form-label">Reboil ratio (Vb/B)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={reboilRatio}
                    onChange={(e) => setReboilRatio(+e.target.value || 0)}
                    className={`form-input ${specFieldClass(
                      reboilerSpec === "boilup"
                    )}`}
                    disabled={solving}
                  />
                  {reboilerSpec !== "boilup" &&
                    Number.isFinite(rebSpec?.boilupRatio_calc) && (
                      <div className="muted small">
                        calc: {rebSpec.boilupRatio_calc.toFixed(3)}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">Top pressure (Pa)</label>
              <input
                type="number"
                value={Ptop}
                onChange={(e) => setPtop(+e.target.value || 0)}
                className="form-input"
                disabled={solving}
              />
            </div>

            <div className="form-row">
              <label className="form-label">ΔP per tray (Pa/tray)</label>
              <input
                type="number"
                value={Pdrop}
                onChange={(e) => setPdrop(+e.target.value || 0)}
                className="form-input"
                disabled={solving}
              />
            </div>
          </div>

          {/* Murphree controls */}
          <div style={{ marginTop: 10 }} className="grid grid-3">
            <div className="form-row">
              <label className="form-label">η_V top</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={etaV_top}
                onChange={(e) => setEtaVTop(+e.target.value || 0)}
                className="form-input"
                disabled={solving}
              />
            </div>
            <div className="form-row">
              <label className="form-label">η_V mid</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={etaV_mid}
                onChange={(e) => setEtaVMid(+e.target.value || 0)}
                className="form-input"
                disabled={solving}
              />
            </div>
            <div className="form-row">
              <label className="form-label">η_V bot</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={etaV_bot}
                onChange={(e) => setEtaVBot(+e.target.value || 0)}
                className="form-input"
                disabled={solving}
              />
            </div>

            <div
              className="form-row"
              style={{
                gridColumn: "1 / span 3",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <input
                id="toggleL"
                type="checkbox"
                checked={showLiquidEta}
                onChange={(e) => setShowLiquidEta(e.target.checked)}
                disabled={solving}
              />
              <label htmlFor="toggleL" className="form-label">
                Enable liquid Murphree (η_L)
              </label>
            </div>

            {showLiquidEta && (
              <>
                <div className="form-row">
                  <label className="form-label">η_L top</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={etaL_top}
                    onChange={(e) => setEtaLTop(+e.target.value || 0)}
                    className="form-input"
                    disabled={solving}
                  />
                </div>
                <div className="form-row">
                  <label className="form-label">η_L mid</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={etaL_mid}
                    onChange={(e) => setEtaLMid(+e.target.value || 0)}
                    className="form-input"
                    disabled={solving}
                  />
                </div>
                <div className="form-row">
                  <label className="form-label">η_L bot</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={etaL_bot}
                    onChange={(e) => setEtaLBot(+e.target.value || 0)}
                    className="form-input"
                    disabled={solving}
                  />
                </div>
              </>
            )}
          </div>

          {/* Draw configuration (editable) */}
          <div className="draw-config" style={{ marginTop: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 12,
              }}
            >
              <div>
                <div className="boundary-title">
                  Draw configuration (product basis)
                </div>
                <div className="muted" style={{ marginTop: 2 }}>
                  Edit <strong>Name</strong>, <strong>Tray</strong> (1=bottom,
                  32=top), and draw rate as <strong>% of feed</strong> or{" "}
                  <strong>kg/h</strong>. The simulator uses only mid-column
                  draws as side draws; overhead/bottoms are handled by
                  condenser/reboiler.
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                <div
                  className="draw-basis"
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <span className="muted" style={{ fontSize: 12 }}>
                    Basis:
                  </span>
                  <label
                    className="muted"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="drawBasis"
                      checked={drawBasis === "pct"}
                      onChange={() => setDrawBasis("pct")}
                      disabled={solving}
                    />
                    % of feed
                  </label>
                  <label
                    className="muted"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="drawBasis"
                      checked={drawBasis === "kgph"}
                      onChange={() => setDrawBasis("kgph")}
                      disabled={solving}
                    />
                    kg/h
                  </label>
                </div>

                <div className="muted" style={{ whiteSpace: "nowrap" }}>
                  Total: <strong>{drawTotalPct.toFixed(1)}%</strong>
                  {Number.isFinite(feedRate) && feedRate > 0 && (
                    <span className="muted" style={{ marginLeft: 8 }}>
                      (
                      {Math.round(
                        (drawTotalPct / 100) * feedRate
                      ).toLocaleString()}{" "}
                      kg/h)
                    </span>
                  )}
                  {Math.abs(drawTotalPct - 100) > 0.5 && (
                    <span style={{ marginLeft: 6 }}>(should be ~100%)</span>
                  )}
                </div>
              </div>
            </div>

            <div className="draw-table">
              <div className="draw-row draw-head">
                <div>Name</div>
                <div>Tray</div>
                <div>{drawBasis === "kgph" ? "Mass (kg/h)" : "Mass %"}</div>
                <div />
              </div>

              {drawConfig.map((row, idx) => (
                <div key={row.id || idx} className="draw-row">
                  <input
                    className="form-input"
                    value={row.name}
                    onChange={(e) =>
                      setDrawConfig((cur) => {
                        const next = cur.slice();
                        next[idx] = { ...next[idx], name: e.target.value };
                        return next;
                      })
                    }
                    disabled={solving}
                  />
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="32"
                    step="1"
                    value={row.tray}
                    onChange={(e) =>
                      setDrawConfig((cur) => {
                        const next = cur.slice();
                        next[idx] = {
                          ...next[idx],
                          tray: Math.max(1, Math.min(32, +e.target.value || 1)),
                        };
                        return next;
                      })
                    }
                    disabled={solving}
                  />
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    step={drawBasis === "kgph" ? "10" : "0.1"}
                    value={
                      drawBasis === "kgph"
                        ? ((Number(row.pct) || 0) / 100) *
                          (Number(feedRate) || 0)
                        : row.pct
                    }
                    onChange={(e) => {
                      const v = Math.max(0, +e.target.value || 0);
                      setDrawConfig((cur) => {
                        const next = cur.slice();
                        if (drawBasis === "kgph") {
                          const fr = Number(feedRate) || 0;
                          const pctVal = fr > 0 ? (100 * v) / fr : 0;
                          next[idx] = { ...next[idx], pct: pctVal };
                        } else {
                          next[idx] = { ...next[idx], pct: v };
                        }
                        return next;
                      });
                    }}
                    disabled={solving}
                  />
                  <button
                    className="btn btn-small"
                    onClick={() =>
                      setDrawConfig((cur) => cur.filter((_, j) => j !== idx))
                    }
                    disabled={solving || drawConfig.length <= 1}
                    title="Remove draw"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                className="btn btn-small"
                onClick={() =>
                  setDrawConfig((cur) => {
                    const next = cur.slice();
                    next.push({
                      id: `${crude}-new-${Date.now()}`,
                      name: "New draw",
                      tray: 16,
                      pct: 0,
                    });
                    return next;
                  })
                }
                disabled={solving}
              >
                + Add draw
              </button>

              <button
                className="btn btn-small"
                onClick={() => {
                  const next = { ...(drawStore || {}) };
                  next[crude] = buildDefaultDrawConfig(crude, 32);
                  setDrawStore(next);
                  saveDrawStore(next);
                  setDirty(true);
                }}
                disabled={solving}
              >
                Reset to defaults
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button onClick={handleSolve} disabled={solving} className="btn">
              {solving ? "Solving…" : "Solve column"}
            </button>
            {dirty && !solving && (
              <span className="muted">
                Inputs changed — click <strong>Solve column</strong> to update.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="layout">
        <div className="pane">
          <DiagnosticsPanel diagnostics={result?.diagnostics} />
          <ColumnView
            result={result}
            drawTrays={drawTraysUI}
            // for UI, use the full crude draw map (including 0 & 31)
            drawSpecs={drawSpecsUI}
            onLog={(s) => appendLog(`[VIEW] ${s}`)}
          />
        </div>

        <div className="pane pane-right">
          <div className="card">
            <div className="card-header">
              <h3>Run summary</h3>
            </div>
            <div className="card-body">
              <ul className="kv">
                <li>
                  <strong>Top Tray T</strong>
                  <span className="temp-chip">
                    {(
                      result.trays?.[result.trays.length - 1]?.T ?? Ttop
                    ).toFixed(1)}{" "}
                    K
                  </span>
                </li>
                <li>
                  <strong>Bottom Tray T</strong>
                  <span className="temp-chip">
                    {(result?.trays?.[0]?.T ?? Tbottom).toFixed(1)} K
                  </span>
                </li>
                <li>
                  <strong>Top P</strong>
                  <span>{(Ptop / 100000).toFixed(3)} bar</span>
                </li>
                <li>
                  <strong>Feed tray</strong>
                  <span>{result.feedTray}</span>
                </li>
                <li>
                  <strong>Status</strong>
                  <span className={solving ? "" : "ok"}>
                    {solving ? "Solving…" : result.status}
                  </span>
                </li>
              </ul>
              <hr />
              <ul className="kv">
                <li>
                  <strong>Qc</strong>
                  <span>{formatPower(energy.Qc_kW)}</span>
                </li>
                <li>
                  <strong>Qr</strong>
                  <span>{formatPower(energy.Qr_kW)}</span>
                </li>
                <li>
                  <strong>Reflux frac</strong>
                  <span>{pct(energy.reflux_fraction)}</span>
                </li>
                <li>
                  <strong>Boil-up frac</strong>
                  <span>{pct(energy.boilup_fraction)}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="card debug-card" style={{ marginTop: 12 }}>
            <div className="card-header">
              <h3>Run log</h3>
            </div>
            <div className="card-body">
              {/* Search row */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="text"
                    value={runLogSearchDraft}
                    onChange={(e) => setRunLogSearchDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRunLogSearch();
                    }}
                    placeholder="Type to search… then press Enter"
                    className="form-input"
                    style={{ flex: 1, padding: "6px 8px" }}
                  />

                  <label
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      userSelect: "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={runLogCaseSensitive}
                      onChange={(e) => setRunLogCaseSensitive(e.target.checked)}
                    />
                    Case
                  </label>

                  <div
                    style={{ minWidth: 80, textAlign: "right", opacity: 0.85 }}
                  >
                    {runLogSearch
                      ? runLogMatchCount > 0
                        ? `${Math.min(
                            runLogMatchIndex + 1,
                            runLogMatchCount
                          )}/${runLogMatchCount}`
                        : "0/0"
                      : ""}
                  </div>
                </div>

                {/* Controls row */}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      className="btn-sm btn-ghost"
                      onClick={() => {
                        if (runLogMatchCount <= 0) return;
                        setRunLogMatchIndex(
                          (i) => (i - 1 + runLogMatchCount) % runLogMatchCount
                        );
                      }}
                      disabled={!runLogSearch || runLogMatchCount <= 0}
                    >
                      Prev
                    </button>

                    <button
                      type="button"
                      className="btn-sm btn-ghost"
                      onClick={() => {
                        if (runLogMatchCount <= 0) return;
                        setRunLogMatchIndex((i) => (i + 1) % runLogMatchCount);
                      }}
                      disabled={!runLogSearch || runLogMatchCount <= 0}
                    >
                      Next
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      className="btn-sm btn-ghost"
                      onClick={clearRunLogSearch}
                      disabled={!runLogSearch}
                    >
                      Clear search
                    </button>

                    <button
                      type="button"
                      className="btn-sm btn-ghost"
                      onClick={clearLog}
                      disabled={!runLog}
                    >
                      Clear log
                    </button>
                  </div>
                </div>
              </div>

              <textarea
                wrap="soft"
                ref={runLogTextAreaRef}
                className="debug-log"
                value={runLog}
                readOnly
                spellCheck={false}
                style={{
                  width: "100%",
                  // When solved, expand the run log so its bottom edge matches the Column window
                  height: !solving && result?.status === "OK" ? 680 : 260,
                  resize:
                    !solving && result?.status === "OK" ? "none" : "vertical",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  overflow: "auto",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
